import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';

import {
  AiConsentOpsRecord,
  AiConsentSenderStanding,
} from '../../../core/attestations/ai-consent.types';
import { BusinessAiConsentService } from '../../../core/attestations/business-ai-consent.service';
import { ModalComponent } from '../../modal/modal';
import { placeRefusal } from '../../modal/modal-refusal';

/** Sender standing on an emailed withdrawal (`FC-DET-058`, `UI-DA-60-B-02`, `-C-02`, `-D-04`). */
export const SENDER_STANDING_OPTIONS: readonly {
  value: AiConsentSenderStanding;
  label: string;
}[] = [
  { value: 'GIVER', label: 'The person who gave it' },
  { value: 'DIRECTOR', label: 'A director on the directors list' },
  { value: 'CIPC_DISCLOSURE', label: 'A director on the latest CIPC disclosure' },
  { value: 'MEMBER_MAY_GIVE', label: 'A member who may give it' },
  { value: 'AGENT', label: 'An introducing agent who may give it' },
  { value: 'UNCONFIRMED', label: 'Could not be confirmed' },
];

const NOT_RECORDED = 'That could not be recorded. Please try again.';

/** An emailed withdrawal, as of when the email arrived (never defaulted to now). */
@Component({
  selector: 'app-consent-email-dialog',
  imports: [
    ModalComponent,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule,
  ],
  template: `
    <app-modal
      heading="Record an emailed withdrawal"
      description="The withdrawal takes effect from when the email arrived."
      submitLabel="Record withdrawal"
      [formGroup]="form"
      [pending]="pending()"
      [error]="error()"
      (submitted)="save()"
    >
      <div class="modal-fields">
        <mat-form-field appearance="outline">
          <mat-label>The email, as received</mat-label>
          <textarea
            matInput
            formControlName="email_text"
            rows="4"
            data-testid="ops-consent-email-text"
          ></textarea>
          @if (form.controls.email_text.hasError('serverError')) {
            <mat-error>{{ form.controls.email_text.getError('serverError') }}</mat-error>
          } @else {
            <mat-error>Paste the email as it was received.</mat-error>
          }
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>When the email arrived</mat-label>
          <input
            matInput
            type="datetime-local"
            formControlName="arrived_at"
            data-testid="ops-consent-arrived-at"
          />
          @if (form.controls.arrived_at.hasError('serverError')) {
            <mat-error>{{ form.controls.arrived_at.getError('serverError') }}</mat-error>
          } @else {
            <mat-error>Enter when the email arrived.</mat-error>
          }
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Sender, as the email shows them</mat-label>
          <input matInput formControlName="sender" />
          <mat-error>{{ form.controls.sender.getError('serverError') }}</mat-error>
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Sender standing</mat-label>
          <mat-select formControlName="sender_standing" data-testid="ops-consent-standing">
            @for (o of standingOptions; track o.value) {
              <mat-option [value]="o.value">{{ o.label }}</mat-option>
            }
          </mat-select>
          <mat-error>{{ form.controls.sender_standing.getError('serverError') }}</mat-error>
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>What the standing rests on</mat-label>
          <input matInput formControlName="standing_basis" />
          <mat-error>{{ form.controls.standing_basis.getError('serverError') }}</mat-error>
        </mat-form-field>
      </div>
    </app-modal>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConsentEmailDialogComponent {
  private readonly entityShortId = inject<string>(MAT_DIALOG_DATA);
  private readonly dialogRef =
    inject<MatDialogRef<ConsentEmailDialogComponent, AiConsentOpsRecord>>(MatDialogRef);
  private readonly service = inject(BusinessAiConsentService);

  protected readonly standingOptions = SENDER_STANDING_OPTIONS;
  protected readonly form = inject(FormBuilder).nonNullable.group({
    email_text: ['', Validators.required],
    arrived_at: ['', Validators.required],
    sender: [''],
    sender_standing: ['' as AiConsentSenderStanding | ''],
    standing_basis: [''],
  });
  protected readonly pending = signal(false);
  protected readonly error = signal<string | null>(null);

  protected save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const v = this.form.getRawValue();
    this.pending.set(true);
    this.error.set(null);
    this.service
      .recordEmailedWithdrawal(this.entityShortId, {
        ...v,
        // datetime-local is the reader's local time; Date converts it to UTC.
        arrived_at: new Date(v.arrived_at).toISOString(),
      })
      .subscribe({
        next: (record) => this.dialogRef.close(record),
        error: (err: unknown) => {
          this.pending.set(false);
          this.error.set(placeRefusal(this.form, err, NOT_RECORDED));
        },
      });
  }
}

/** The director check after a withdrawal. Recording it does not change the withdrawal. */
@Component({
  selector: 'app-consent-check-dialog',
  imports: [
    ModalComponent,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule,
  ],
  template: `
    <app-modal
      heading="Record the director check"
      description="The withdrawal has already taken effect; recording the result does not change it."
      submitLabel="Record check"
      [formGroup]="form"
      [pending]="pending()"
      [error]="error()"
      (submitted)="save()"
    >
      <div class="modal-fields">
        <mat-form-field appearance="outline">
          <mat-label>CIPC disclosure checked</mat-label>
          <input matInput formControlName="cipc_disclosure" />
          @if (form.controls.cipc_disclosure.hasError('serverError')) {
            <mat-error>{{ form.controls.cipc_disclosure.getError('serverError') }}</mat-error>
          } @else {
            <mat-error>Name the CIPC disclosure you checked.</mat-error>
          }
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Its date</mat-label>
          <input matInput type="date" formControlName="cipc_disclosure_date" />
          @if (form.controls.cipc_disclosure_date.hasError('serverError')) {
            <mat-error>{{ form.controls.cipc_disclosure_date.getError('serverError') }}</mat-error>
          } @else {
            <mat-error>Enter the date of the disclosure.</mat-error>
          }
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Result</mat-label>
          <mat-select formControlName="result">
            <mat-option value="SHOWN">Shown as a director</mat-option>
            <mat-option value="NOT_SHOWN">Not shown as a director</mat-option>
            <mat-option value="NOT_ESTABLISHED">Could not be established</mat-option>
          </mat-select>
          @if (form.controls.result.hasError('serverError')) {
            <mat-error>{{ form.controls.result.getError('serverError') }}</mat-error>
          } @else {
            <mat-error>Choose the result.</mat-error>
          }
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>What it rested on</mat-label>
          <input matInput formControlName="basis" />
          <mat-error>{{ form.controls.basis.getError('serverError') }}</mat-error>
        </mat-form-field>
      </div>
    </app-modal>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConsentCheckDialogComponent {
  private readonly entityShortId = inject<string>(MAT_DIALOG_DATA);
  private readonly dialogRef =
    inject<MatDialogRef<ConsentCheckDialogComponent, AiConsentOpsRecord>>(MatDialogRef);
  private readonly service = inject(BusinessAiConsentService);

  protected readonly form = inject(FormBuilder).nonNullable.group({
    cipc_disclosure: ['', Validators.required],
    cipc_disclosure_date: ['', Validators.required],
    result: ['' as 'SHOWN' | 'NOT_SHOWN' | 'NOT_ESTABLISHED' | '', Validators.required],
    basis: [''],
  });
  protected readonly pending = signal(false);
  protected readonly error = signal<string | null>(null);

  protected save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const v = this.form.getRawValue();
    this.pending.set(true);
    this.error.set(null);
    this.service
      .recordDirectorCheck(this.entityShortId, {
        ...v,
        result: v.result as 'SHOWN' | 'NOT_SHOWN' | 'NOT_ESTABLISHED',
      })
      .subscribe({
        next: (record) => this.dialogRef.close(record),
        error: (err: unknown) => {
          this.pending.set(false);
          this.error.set(placeRefusal(this.form, err, NOT_RECORDED));
        },
      });
  }
}
